import {
  safeLocalStorageGetItem,
  safeLocalStorageSetItem,
} from '@/utils/safeLocalStorage';

const STORAGE_KEY = 'indmo-content-cache-generation';

/**
 * Bump `VITE_CACHE_GENERATION` when API shapes, serializers, or client parsing change.
 * IndexedDB is not deleted; a soft contract refresh refetches listing and details (see docs/CACHING.md).
 */
export function getExpectedCacheGeneration(): string {
  const v = import.meta.env.VITE_CACHE_GENERATION;
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : '2';
}

/** True until localStorage reflects the current build’s generation (after a successful listing 200). */
export function needsContractListingRefresh(): boolean {
  return readStoredGeneration() !== getExpectedCacheGeneration();
}

/** Call after a successful listing merge so normal If-None-Match revalidation resumes. */
export function markContractGenerationApplied(): void {
  writeStoredGeneration(getExpectedCacheGeneration());
}

/** Safe segment for Dexie database name (dev branch isolation). */
export function sanitizeDbNameSegment(value: string): string {
  const s = value.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 48);
  return s || 'default';
}

/**
 * Production uses a fixed name. In dev, optionally set `VITE_DEV_BRANCH` in `.env.local`
 * so each branch/worktree gets its own IndexedDB.
 */
export function getContentDbName(): string {
  if (import.meta.env.DEV) {
    const branch = import.meta.env.VITE_DEV_BRANCH;
    const raw = typeof branch === 'string' && branch.trim() !== '' ? branch.trim() : 'default';
    return `ContentDb-${sanitizeDbNameSegment(raw)}`;
  }
  return 'ContentDb';
}

/**
 * In development, skip If-None-Match / If-Modified-Since so the API always returns full
 * bodies when possible; avoids stale 304s when switching branches against the same CMS data.
 */
export function shouldSendHttpCacheValidators(): boolean {
  return import.meta.env.PROD;
}

function readStoredGeneration(): string | null {
  return safeLocalStorageGetItem(STORAGE_KEY);
}

function writeStoredGeneration(value: string): void {
  safeLocalStorageSetItem(STORAGE_KEY, value);
}

/**
 * No-op compatibility hook before opening Dexie (soft invalidation does not wipe IndexedDB).
 */
export async function ensureStaleContentCacheCleared(): Promise<void> {
  // Contract mismatch is handled via needsContractListingRefresh() + detailClientGeneration stamps.
}
